import { ComponentType, FC } from "react";

import {
  BoxProps,
  Badge,
  Column,
  IconBox,
  IconBoxProps,
  Row,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";

import { Icon as ManagedColumnIcon } from "src/components/layout/nav/icons/lifecycle-studio";
import { ColumnType, ModelSemanticType } from "src/types/visual";
import { FilterOption, MetadataType, ROW_HEIGHT } from "./constants";
import { getColumnIcon } from "./utils";
import { type FilterPopoverOptionBadge } from "./types";

type FilterPopoverOptionProps = {
  isFocused?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  isHightouchManaged?: boolean;
  label: string;
  description?: string | null;
  iconProps: IconBoxProps;
  optionType: FilterOption | undefined;
  columnType: ColumnType | undefined;
  metadata?: { modelName: string; type: MetadataType } | null;
  semanticType?: ModelSemanticType | null;
  useIconBox?: boolean;
  badge?: FilterPopoverOptionBadge;
  onClick: () => void;
  onFocus: () => void;
  // Use onPointerEnter instead of onMouseEnter to trigger when button is disabled
  onPointerEnter?: () => void;
};

export const FilterPopoverOption: FC<
  Readonly<FilterPopoverOptionProps & BoxProps>
> = ({
  optionType,
  columnType,
  description,
  iconProps,
  isFocused = false,
  isSelected = false,
  isDisabled = false,
  isHightouchManaged = false,
  label,
  metadata,
  sx = {},
  semanticType = null,
  useIconBox = true,
  badge,
  ...props
}) => {
  const { appHouseholdingEnabled } = useFlags();
  const ColumnIcon: ComponentType | null = isHightouchManaged
    ? ManagedColumnIcon
    : optionType
      ? getColumnIcon({
          type: optionType,
          columnType,
          semanticType: appHouseholdingEnabled ? semanticType : null,
        })
      : null;

  const icon = useIconBox ? (
    <IconBox isSelected={isSelected} {...iconProps} />
  ) : (
    iconProps.icon
  );

  const selectedBg = `${iconProps.color}.50`;
  const selectedTextColor = `${iconProps.color}.600`;

  return (
    <Row
      className="group"
      as="button"
      align="center"
      justify="space-between"
      role="option"
      aria-disabled={isDisabled}
      disabled={isDisabled}
      bg={
        isSelected ? selectedBg : isFocused ? "base.background" : "transparent"
      }
      borderWidth="hairline"
      borderStyle="solid"
      borderColor="transparent"
      borderRadius="2xl"
      height={ROW_HEIGHT}
      maxHeight={ROW_HEIGHT}
      outline="none !important"
      width="100%"
      transitionProperty="border-color, background-color"
      transitionDuration="fast"
      transitionTimingFunction="easeOutQuad"
      sx={sx}
      _focus={{ boxShadow: "inset 0 0 0 4px #E0F3F5" }}
      _focusWithin={{
        ".column-type-icon": {
          color: isSelected ? selectedTextColor : "text.primary",
        },
      }}
      _hover={{
        bg: isSelected ? selectedBg : "base.background",
      }}
      _active={{
        bg: isSelected ? selectedBg : "base.background",
        boxShadow: "none",
      }}
      _disabled={{
        cursor: "not-allowed",
        bg: "white",
        color: "text.secondary",
        _hover: {
          bg: isSelected ? selectedBg : "base.background",
        },
      }}
      {...props}
    >
      <Row align="center" width="100%" gap={4} p={2}>
        <Row
          align="center"
          gap={2}
          // `9` represents the width of ColumnIcon (5) + the gap (4)
          width={ColumnIcon ? "calc(100% - var(--chakra-sizes-9))" : "100%"}
          color={isSelected ? selectedTextColor : "text.primary"}
          opacity={isDisabled ? 0.4 : 1}
        >
          {icon}
          <Column
            justify="start"
            overflow="hidden"
            sx={{ span: { textAlign: "left" } }}
          >
            <Row align="center" gap={2} overflow="hidden">
              <TextWithTooltip
                fontWeight="medium"
                color={isDisabled ? "text.secondary" : "inherit"}
              >
                {label}
              </TextWithTooltip>
              {badge && (
                <Badge size="sm" variant={badge.variant} svgIcon={badge.icon}>
                  {badge.label}
                </Badge>
              )}
            </Row>
            {description && (
              <Text
                isTruncated
                color={isSelected ? selectedTextColor : "text.tertiary"}
                size="sm"
              >
                {description}
              </Text>
            )}
          </Column>
        </Row>

        {ColumnIcon && (
          <Row
            align="center"
            color={isSelected ? selectedTextColor : "text.tertiary"}
            fontSize="20px"
            flexShrink={0}
            _groupHover={{
              color: isSelected ? selectedTextColor : "text.primary",
            }}
          >
            <ColumnIcon />
          </Row>
        )}
      </Row>
    </Row>
  );
};
